'use client';

import Image from 'next/image';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCartModalContext } from '@/app/context/CartSidebarModalContext';
import {
  removeAllItemsFromCart,
  removeItemFromCart,
  selectTotalPrice,
  updateCartItemQuantity,
} from '@/redux/features/cart-slice';
import type { RootState } from '@/redux/store';
import { createTranslator } from '@/lib/localization';

type Props = {
  texts?: Record<string, string> | null;
  /** The domain's price policy (Core, via the shell): false = this modal renders no price (item price, subtotal). Default: shown. */
  showPrice?: boolean;
};

export function CartSidebarModal({ texts, showPrice = true }: Props) {
  const t = createTranslator(texts ?? {});
  const dispatch = useDispatch();
  const { isCartModalOpen, closeCartModal } = useCartModalContext();
  const cartItems = useSelector((state: RootState) => state.cartReducer.items);
  const total = useSelector(selectTotalPrice);

  useEffect(() => {
    if (!isCartModalOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeCartModal();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isCartModalOpen, closeCartModal]);

  return (
    <div
      className={`fixed inset-0 z-[130] transition-all duration-300 ${
        isCartModalOpen ? 'pointer-events-auto bg-black/50 opacity-100' : 'pointer-events-none bg-black/0 opacity-0'
      }`}
      onClick={closeCartModal}
    >
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-md transform bg-white shadow-3 transition-transform duration-300 ${
          isCartModalOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-gray-3 px-5 py-4">
            <h3 className="text-lg font-semibold text-dark">{t('cart.title')}</h3>
            <button
              type="button"
              onClick={closeCartModal}
              aria-label={t('cart.close')}
              className="rounded-full border border-gray-3 px-3 py-1 text-sm font-semibold text-dark hover:text-primary"
            >
              X
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            {cartItems.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-3 bg-gray-1 p-6 text-center">
                <p className="text-dark-3">{t('cart.empty')}</p>
                <button
                  type="button"
                  onClick={closeCartModal}
                  className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
                >
                  {t('cart.continueShopping')}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {cartItems.map((item) => {
                  const image = item.imgs?.thumbnails?.[0] || item.imgs?.previews?.[0] || '/onlineexpotheme/content/images/slide01-min.jpg';
                  return (
                    <article key={item.id} className="rounded-xl border border-gray-3 p-3">
                      <div className="flex gap-3">
                        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-1">
                          <Image src={image} alt={item.title} fill sizes="80px" className="object-cover" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="line-clamp-2 text-sm font-semibold text-dark">{item.title}</h4>
                          {showPrice ? <p className="mt-1 text-sm text-primary">${item.discountedPrice.toFixed(2)}</p> : null}

                          <div className="mt-2 flex items-center justify-between gap-2">
                            <div className="inline-flex items-center rounded-lg border border-gray-3">
                              <button
                                type="button"
                                onClick={() =>
                                  dispatch(
                                    updateCartItemQuantity({ id: item.id, quantity: Math.max(1, item.quantity - 1) })
                                  )
                                }
                                className="px-2 py-1 text-dark hover:text-primary"
                              >
                                -
                              </button>
                              <span className="px-2 py-1 text-sm text-dark">{item.quantity}</span>
                              <button
                                type="button"
                                onClick={() =>
                                  dispatch(updateCartItemQuantity({ id: item.id, quantity: item.quantity + 1 }))
                                }
                                className="px-2 py-1 text-dark hover:text-primary"
                              >
                                +
                              </button>
                            </div>

                            <button
                              type="button"
                              onClick={() => dispatch(removeItemFromCart(item.id))}
                              className="text-xs font-semibold text-error hover:underline"
                            >
                              {t('cart.remove')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-gray-3 p-5">
            {showPrice ? (
              <div className="mb-3 flex items-center justify-between text-sm text-dark-3">
                <span>{t('cart.subtotal')}</span>
                <span className="text-base font-semibold text-dark">${total.toFixed(2)}</span>
              </div>
            ) : null}

            {/* No Checkout link: the storefront has no /checkout page (quotes go through the RFQ flow), so a link there was a
                known 404 (Phase 13 decision — see docs/navigation-decisions.md). */}
            <div className="grid grid-cols-1 gap-3">
              <button
                type="button"
                onClick={() => dispatch(removeAllItemsFromCart())}
                className="rounded-lg border border-gray-3 px-4 py-2 text-sm font-semibold text-dark hover:border-error hover:text-error"
                disabled={cartItems.length === 0}
              >
                {t('cart.clearCart')}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}
